/**
 * context/AuthContext.jsx
 * =======================
 * Global auth state. Provides current user, login/logout,
 * and token-aware fetch helper used by all API calls.
 */

import { createContext, useContext, useState, useCallback } from "react";

const AuthContext = createContext(null);

const TOKEN_KEY = "gradiance_token";
const REFRESH_KEY = "gradiance_refresh";
const USER_KEY = "gradiance_user";
const DEMO_NAME_KEY = "gradiance_demo_name";

// ── Demo "view as test student" bootstrap ────────────────────────────────────
// A "View as" link opens /student?demo_token=<jwt> in a NEW TAB. We must not
// touch localStorage here: it's shared across same-origin tabs, so writing the
// demo token there would replace the instructor's token and log them out in
// their main tab. Instead we stash the demo token in sessionStorage, which is
// per-tab — this tab authenticates as the test student, every other tab keeps
// its own session. Runs once at module load, before AuthProvider reads state,
// and strips the param from the URL so it isn't left lying around.
(function bootstrapDemoToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const demo = params.get("demo_token");
    if (demo) {
      sessionStorage.setItem(TOKEN_KEY, demo);
      // Optional display name so the demo tab can show the specific student.
      const demoName = params.get("demo_name");
      if (demoName) sessionStorage.setItem(DEMO_NAME_KEY, demoName);
      params.delete("demo_token");
      params.delete("demo_name");
      const qs = params.toString();
      const clean =
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", clean);
    }
  } catch {
    /* no-op: SSR or storage disabled */
  }
})();

// Read the active token, preferring a per-tab demo token over the shared one.
function readToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

// Is this tab running as a demo (view-as) session?
function isDemoTab() {
  try {
    return !!sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return false;
  }
}

// Decode a JWT payload (no verification — the server verifies on every call;
// this is only to populate a minimal user object so route guards that check
// `user` don't bounce a demo tab to login). Returns { id, role } or null.
function userFromToken(tok) {
  try {
    const payload = tok.split(".")[1];
    const json = JSON.parse(
      atob(
        payload
          .replace(/-/g, "+")
          .replace(/_/g, "/")
          .padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "="),
      ),
    );
    const id = payload && json.sub != null ? Number(json.sub) : null;
    let name = "Test Student";
    try {
      name = sessionStorage.getItem(DEMO_NAME_KEY) || name;
    } catch {
      /* ignore */
    }
    return { id, role: json.role, is_test: true, display_name: name };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // In a demo (view-as) tab the token is a test student's, but localStorage
    // still holds the instructor's cached user. Don't use that — instead build a
    // minimal student user from the token claims, so route guards that check
    // `user` see a logged-in student (not the instructor, and not null → login).
    if (isDemoTab()) {
      const tok = readToken();
      return tok ? userFromToken(tok) : null;
    }
    try {
      return JSON.parse(localStorage.getItem(USER_KEY));
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => readToken());

  const login = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(REFRESH_KEY, data.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    // Clear the per-tab demo token first (if this is a view-as tab), then the
    // shared session. A demo tab logging out never disturbs the instructor tab's
    // localStorage token beyond what it would normally clear.
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(DEMO_NAME_KEY);
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  /**
   * Merge fields into the cached user after the account changes elsewhere —
   * a profile save, for instance — so the header doesn't keep showing the old
   * display name until the next reload.
   *
   * Deliberately does NOT write localStorage in a view-as tab. There,
   * localStorage still holds the INSTRUCTOR's cached user while this tab is
   * authenticated as a test student; persisting here would rename the
   * instructor in their own tab. State-only is correct for a demo tab, which
   * is throwaway by nature.
   */
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      if (!isDemoTab()) {
        try {
          localStorage.setItem(USER_KEY, JSON.stringify(next));
        } catch {
          /* storage disabled — in-memory state is still correct */
        }
      }
      return next;
    });
  }, []);

  /**
   * Token-aware fetch. Automatically adds Authorization header.
   * Usage: await apiFetch('/api/queue/1/join', { method: 'POST' })
   */
  const apiFetch = useCallback(
    async (path, options = {}) => {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      };

      const res = await fetch(path, { ...options, headers });

      // Handle policy violations (Nedry modal trigger)
      if (res.status === 422) {
        const data = await res.json();
        if (data.detail?.violation) {
          const err = new Error(data.detail.message);
          err.isViolation = true;
          err.code = data.detail.code;
          throw err;
        }
      }

      if (res.status === 401) {
        logout();
        throw new Error("Session expired. Please log in again.");
      }

      return res;
    },
    [token, logout],
  );

  return (
    <AuthContext.Provider
      value={{ user, token, login, logout, updateUser, apiFetch }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
